/*
The chartjs-adapter-luxon is missing its TypeScript declaration.

Issue open here: https://github.com/chartjs/chartjs-adapter-luxon/issues/59

One solution is to add an external-declarations.d.ts file with content

    declare module "chartjs-adapter-luxon";

TODO: if this issue get fixed on chart.js side, update chartjs-adapter-luxon
and remove the custom module declaration and this comment.
*/

import "chartjs-adapter-luxon";
import { useLayoutEffect, useRef } from "react";
import {
  ActiveDataPoint,
  ArcElement,
  Chart as ChartJs,
  ChartConfiguration,
  Plugin,
  CategoryScale,
  LineController,
  LineElement,
  LinearScale,
  PieController,
  PointElement,
  TimeScale,
  Tooltip,
  BarController,
  BarElement,
} from "chart.js";

export type {
  ActiveDataPoint as ChartDataPoint,
  ChartConfiguration,
} from "chart.js";

export type ChartProps = {
  /**
   * Chart.js configuration, reference: https://www.chartjs.org/docs/latest/configuration/
   */
  configuration: ChartConfiguration;

  onClick?: (point: ActiveDataPoint) => void;
};

ChartJs.defaults.font.family = "Nunito";

// Plugins.
// ///////////////////////////////////////////////////////////////////

const emptyDataPlugin: Plugin = {
  id: "emptyData",
  afterDraw(chart) {
    const {
      chartArea: { left, top, right, bottom },
      ctx,
      data: { datasets },
    } = chart;

    const hasData = datasets.length > 0;

    if (hasData) return;

    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;

    const text = "No data to display";

    chart.clear();
    ctx.save();
    ctx.fillStyle = "#D7D7D7";
    ctx.font = "24px Nunito";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, centerX, centerY);
    ctx.restore();
  },
};

ChartJs.register(
  ArcElement,
  CategoryScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PieController,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  emptyDataPlugin,
);

// Component.
// ///////////////////////////////////////////////////////////////////

export const Chart: React.FC<ChartProps> = ({ configuration: { options, ...configuration }, onClick }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<ChartJs | null>(null);

  useLayoutEffect(() => {
    if (chartRef.current !== null) {
      chartRef.current.destroy();
    }

    if (canvasRef.current === null) return;

    const canvas = canvasRef.current;

    const context2d = canvas.getContext("2d");
    if (context2d === null) return;

    const chart = new ChartJs(context2d, {
      ...configuration,
      options: {
        ...options,
        animation: false,
        interaction: {
          intersect: true,
          mode: "nearest",
        },
        onClick: (event, _elements, chart: ChartJs) => {
          if (event.native) {
            const elements = chart.getElementsAtEventForMode(event.native, "nearest", { intersect: true }, true);

            if (Array.isArray(elements) && elements.length === 1) {
              const { datasetIndex, index } = elements[0];
              onClick?.({
                datasetIndex,
                index,
              });
            }
          }
        },
        maintainAspectRatio: false,
        responsive: true,
      },
    });

    chartRef.current = chart;
  }, [configuration, options, onClick]);

  return <canvas ref={canvasRef} />;
};
